import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PanelState, { extractApiError } from "@/components/PanelState";
import { parseL10ImportFile } from "@/lib/l10Import";
import {
  Settings as SettingsIcon, Plus, X, Check, ChevronDown, ChevronUp,
  Circle, CheckCircle2, AlertTriangle, RefreshCw, Radio, Upload,
  ChevronLeft, ChevronRight, CalendarDays, Pencil,
} from "lucide-react";

// -------- Week helpers (shared by L10 page + selector) --------
// Return the Monday ISO date (YYYY-MM-DD) for the calendar week that
// contains `dateObj`. Uses local time so "this Monday" matches the user's
// wall clock rather than UTC (matters for late-night sessions).
function mondayOf(dateObj) {
  const d = new Date(dateObj);
  const dow = d.getDay(); // 0=Sun ... 6=Sat
  const daysBack = dow === 0 ? 6 : dow - 1;
  const m = new Date(d.getFullYear(), d.getMonth(), d.getDate() - daysBack);
  const yyyy = m.getFullYear();
  const mm = String(m.getMonth() + 1).padStart(2, "0");
  const dd = String(m.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function todayMondayISO() { return mondayOf(new Date()); }
function addDaysISO(iso, days) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return mondayOf(d);
}
function formatWeekLabel(iso, todayMon) {
  const d = new Date(iso + "T00:00:00");
  const end = new Date(d.getTime() + 6 * 86400000);
  const fmt = (x) => x.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
  const yr = d.getFullYear();
  const base = `${fmt(d)}–${fmt(end)}, ${yr}`;
  if (iso === todayMon) return `${base} · This week`;
  const diffWeeks = Math.round((new Date(todayMon) - d) / (7 * 86400000));
  if (diffWeeks === 1) return `${base} · Last week`;
  return base;
}

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtN = (n, unit) => {
  if (n === null || n === undefined || n === "") return "—";
  if (unit === "$") return fmt$(n);
  if (unit === "%") return `${Number(n).toFixed(1)}%`;
  if (unit === "d") return `${n}d`;
  return String(n);
};

// Status pill tokens — saturated & bold. Same palette as the app's other
// prominent status surfaces (bold enough to read at a glance, not soft pastels).
const STATUS_STYLES = {
  ON_TARGET: "bg-[#dcfce7] text-[#166534] border-[#4ade80] font-bold",
  WATCH:     "bg-[#fef3c7] text-[#854d0e] border-[#facc15] font-bold",
  OFF_TRACK: "bg-[#fee2e2] text-[#991b1b] border-[#f87171] font-bold",
  NO_DATA:   "bg-[#e5e7eb] text-[#4b5563] border-[#9ca3af] font-bold",
  MANUAL:    "bg-[#dbeafe] text-[#1e40af] border-[#60a5fa] font-bold",
};

function StatusBadge({ status }) {
  if (!status) return null;
  const cls = STATUS_STYLES[status] || STATUS_STYLES.NO_DATA;
  const label = status.replace(/_/g, " ");
  return (
    <span className={`inline-flex text-[10.5px] font-mono-data uppercase tracking-widest border rounded px-2 py-0.5 ${cls}`}
          data-testid={`l10-badge-${status}`}>
      {label}
    </span>
  );
}

function PersistentTag({ weeks }) {
  if (!weeks) return null;
  return (
    <span className="ml-2 inline-flex text-[10.5px] font-mono-data font-bold uppercase tracking-widest bg-[#fee2e2] text-[#991b1b] border border-[#f87171] rounded px-2 py-0.5"
          data-testid="l10-persistent-tag">
      PERSISTENT ({weeks}wk)
    </span>
  );
}

// --------- Segment shell (matches the app's card style) ----------
function Segment({ index, title, meta, children, defaultOpen = true, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      data-testid={testId}
      className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[#fafbfc] text-left"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[#0072bc] text-white font-mono-data font-bold text-[12px]"
                data-testid={`l10-segment-badge-${index}`}>
            {index}
          </span>
          <span className="font-display font-bold text-[16px] text-[#0e1420]">{title}</span>
          {meta && <span className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">{meta}</span>}
        </div>
        {open ? <ChevronUp size={16} className="text-[color:var(--cc-ink-muted)]" /> : <ChevronDown size={16} className="text-[color:var(--cc-ink-muted)]" />}
      </button>
      {open && <div className="px-5 pb-5 pt-1 border-t border-[#eef1f6]">{children}</div>}
    </section>
  );
}

// ================= L10 root =================
export default function L10() {
  const [scorecard, setScorecard] = useState(null);
  const [vpsSnapshot, setVpsSnapshot] = useState(null);
  const [segue, setSegue] = useState(null);
  const [headlines, setHeadlines] = useState({ auto: [], manual: [] });
  const [rocks, setRocks] = useState([]);
  const [todos, setTodos] = useState([]);
  const [ids, setIds] = useState([]);
  const [meeting, setMeeting] = useState(null);
  const [manualMetrics, setManualMetrics] = useState({});
  const [thresholdsOpen, setThresholdsOpen] = useState(false);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [stagedTodos, setStagedTodos] = useState([]);

  // Week selector state — URL-synced so refreshes / shared links are stable.
  // Landing default (URL empty): "latest_reporting" — the server-resolved
  // last-completed week. Preserves the historical KPI-review landing
  // experience. Any explicit week (past or current) is stored as YYYY-MM-DD.
  // Added 2026-07-29 per Brett — L10 needed same-day access to the
  // current-in-progress week's meeting minutes (Conclude + Todos + Import),
  // which the "strictly prior week" default was hiding.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlWeek = searchParams.get("week");
  const selectedWeek = urlWeek || "latest_reporting";
  const setSelectedWeek = useCallback((newVal) => {
    const params = new URLSearchParams(searchParams);
    if (!newVal || newVal === "latest_reporting") params.delete("week");
    else params.set("week", newVal);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadAll = useCallback(async () => {
    setLoadState({ status: "loading", error: null });
    try {
      const p = { params: { week: selectedWeek } };
      const [sc, vs, sg, hl, rk, td, id, mt, mm] = await Promise.all([
        api.get("/l10/scorecard", p),
        api.get("/l10/scorecard/vps-snapshot", p),
        api.get("/l10/segue", p),
        api.get("/l10/headlines", p),
        api.get("/l10/rocks", p),
        api.get("/l10/todos"),                    // persistent, no week filter
        api.get("/l10/ids", p),
        api.get("/l10/meeting", p),
        api.get("/l10/manual-metrics", p),
      ]);
      setScorecard(sc.data);
      setVpsSnapshot(vs.data);
      setSegue(sg.data);
      setHeadlines(hl.data || { auto: [], manual: [] });
      setRocks(rk.data || []);
      setTodos(td.data || []);
      setIds(id.data || []);
      setMeeting(mt.data);
      setManualMetrics(mm.data || {});
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setLoadState({ status: "error", error: extractApiError(err) });
    }
  }, [selectedWeek]);
  useEffect(() => { loadAll(); }, [loadAll]);

  if (loadState.status !== "ok" || !scorecard) {
    return (
      <PanelState
        status={loadState.status}
        error={loadState.error}
        onRetry={loadAll}
        testIdPrefix="l10"
      />
    );
  }

  const weekRange = (() => {
    const d = new Date(scorecard.week_of + "T00:00:00");
    const end = new Date(d.getTime() + 6 * 86400000);
    const fmt = (x) => x.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
    return `Week of ${fmt(d)} – ${fmt(end)}`;
  })();
  const lastUpdated = meeting?.updated_at ? new Date(meeting.updated_at) : new Date();

  return (
    <div data-testid="l10-page" className="space-y-5">
      <Header
        weekRange={weekRange}
        lastUpdated={lastUpdated}
        onThresholds={() => setThresholdsOpen(true)}
        resolvedWeekOf={scorecard.week_of}
        selectedWeek={selectedWeek}
        onSelectWeek={setSelectedWeek}
      />

      <div className="space-y-4">
        <Segment index="1" title="Segue" meta="AUTO · WEEK HIGHLIGHTS" testId="l10-segue">
          <SegueBody segue={segue} weekRange={weekRange} />
        </Segment>

        <Segment index="2" title="Scorecard" meta="AUTO + MANUAL ENTRY" testId="l10-scorecard">
          <VpsSnapshotStrip
            vps={vpsSnapshot}
            computed={scorecard}
            onRefresh={async () => {
              const vs = await api.get("/l10/scorecard/vps-snapshot");
              setVpsSnapshot(vs.data);
            }}
          />
          <ScorecardBody
            scorecard={scorecard}
            manual={manualMetrics}
            onManualSave={async (updates) => {
              // manual-metrics is week-scoped — reuse the page's selectedWeek
              // so writes land on the same week the user is viewing.
              const p = { params: { week: selectedWeek } };
              const { data } = await api.patch("/l10/manual-metrics", updates, p);
              setManualMetrics(data);
              const sc = await api.get("/l10/scorecard", p);
              setScorecard(sc.data);
            }}
          />
          <TechPerformance
            techRows={scorecard.tech_rows}
            weekEnding={scorecard.week_ending}
            snapshotUpdatedAt={scorecard.snapshot_updated_at}
            snapshotStatus={scorecard.snapshot_status}
          />
        </Segment>

        <Segment index="3" title="Rock Review" meta={`${rocks[0]?.quarter || ""} · EDITABLE`} testId="l10-rocks">
          <RocksBody rocks={rocks} setRocks={setRocks} reload={loadAll} />
        </Segment>

        <Segment index="4" title="Customer & Employee Headlines" meta="AUTO + MANUAL" testId="l10-headlines">
          <HeadlinesBody headlines={headlines} setHeadlines={setHeadlines} resolvedWeekOf={scorecard.week_of} />
        </Segment>

        <Segment index="5" title="To-Do List" meta="PERSISTENT · CARRY-FORWARD" testId="l10-todos">
          <TodosBody
            todos={todos}
            setTodos={setTodos}
            reload={loadAll}
            stagedTodos={stagedTodos}
            setStagedTodos={setStagedTodos}
          />
        </Segment>

        <Segment index="6" title="IDS" meta="IDENTIFY · DISCUSS · SOLVE" testId="l10-ids">
          <IdsBody ids={ids} setIds={setIds} reload={loadAll} />
        </Segment>

        <Segment index="7" title="Conclude" meta="RATING · CASCADE · CLOSE" testId="l10-conclude">
          <ConcludeBody
            meeting={meeting}
            setMeeting={setMeeting}
            currentWeekOf={scorecard.week_of}
            selectedWeek={selectedWeek}
            onImportedTodos={setStagedTodos}
          />
        </Segment>
      </div>

      <footer className="pt-4 text-center text-[11px] font-mono-data text-[color:var(--cc-ink-muted)]">
        MRE-Agents · Agent 7 · L10 Dashboard · Auto-generated Tuesday 6AM
      </footer>

      {thresholdsOpen && (
        <ThresholdsModal
          thresholds={scorecard.thresholds}
          onClose={() => setThresholdsOpen(false)}
          onSave={async (updates) => {
            const { data } = await api.patch("/l10/thresholds", updates);
            const sc = await api.get("/l10/scorecard");
            setScorecard(sc.data);
            setThresholdsOpen(false);
            return data;
          }}
        />
      )}
    </div>
  );
}

// ================= Header =================
function Header({ weekRange, lastUpdated, onThresholds, resolvedWeekOf, selectedWeek, onSelectWeek }) {
  const stamp = lastUpdated.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
  });
  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <div className="text-[11px] uppercase tracking-[.15em] text-[#0072bc] font-bold">
          Claire · Agent 7 · Tuesdays 6 AM
        </div>
        <h1 className="font-display font-bold text-[26px] sm:text-[34px] tracking-tight text-[#0e1420] mt-1">
          MRE — <span className="text-[#0072bc]">L10</span> Meeting
        </h1>
        <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1">{weekRange}</p>
      </div>
      <div className="flex flex-col items-end gap-2">
        <WeekSelector
          resolvedWeekOf={resolvedWeekOf}
          selectedWeek={selectedWeek}
          onSelectWeek={onSelectWeek}
        />
        <div className="text-[11.5px] font-mono-data text-[color:var(--cc-ink-muted)]" data-testid="l10-last-updated">
          Last updated: {stamp}
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[10.5px] font-mono-data font-bold uppercase tracking-widest bg-[#dcfce7] text-[#166534] border border-[#4ade80] rounded px-2 py-0.5"
                data-testid="l10-synced">
            <Check size={11} strokeWidth={2.4} /> synced
          </span>
          <button
            onClick={onThresholds}
            data-testid="l10-thresholds-btn"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-[#e7e9ee] bg-white text-[#0e1420] hover:bg-[#f5f6f8]"
          >
            <SettingsIcon size={13} strokeWidth={1.8} /> Thresholds
          </button>
        </div>
      </div>
    </header>
  );
}

// ================= WeekSelector =================
// URL-synced week picker: prev / next arrows + dropdown of the last 12
// weeks + a "This week" pill shortcut. Backend accepts week=YYYY-MM-DD
// verbatim (see server.py _resolve_week_of), so we just push the ISO
// Monday. Default (URL empty) keeps `latest_reporting` — historical
// KPI-review landing behavior. Users navigate to the current-in-progress
// week to enter same-day meeting minutes / Imports.
function WeekSelector({ resolvedWeekOf, selectedWeek, onSelectWeek }) {
  const todayMon = todayMondayISO();
  // Concrete resolved week we're currently viewing. Prefer the server's
  // resolved value (accurate for `latest_reporting`) — falls back to
  // the URL param when the resolved value isn't back yet.
  const currentIso = resolvedWeekOf || (selectedWeek === "latest_reporting" ? todayMon : selectedWeek);
  const options = useMemo(() => {
    // 12 weeks back through this week (13 rows total), newest first.
    const out = [];
    let cur = todayMon;
    for (let i = 0; i < 13; i += 1) {
      out.push({ value: cur, label: formatWeekLabel(cur, todayMon) });
      cur = addDaysISO(cur, -7);
    }
    // Ensure the currently-viewed week is in the list even if it's older
    // than 12 weeks (e.g. shared link to an older week).
    if (currentIso && !out.some((o) => o.value === currentIso)) {
      out.push({ value: currentIso, label: formatWeekLabel(currentIso, todayMon) });
    }
    return out;
  }, [todayMon, currentIso]);

  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const isOnCurrentWeek = currentIso === todayMon;
  const nextDisabled = currentIso >= todayMon;

  const goPrev = () => onSelectWeek(addDaysISO(currentIso, -7));
  const goNext = () => { if (!nextDisabled) onSelectWeek(addDaysISO(currentIso, 7)); };
  const goThis = () => onSelectWeek(todayMon);
  const pick = (iso) => { onSelectWeek(iso); setOpen(false); };

  return (
    <div className="flex items-center gap-1" ref={rootRef} data-testid="l10-week-selector">
      <button
        onClick={goPrev}
        aria-label="Previous week"
        data-testid="l10-week-prev"
        className="p-1.5 rounded border border-[#e7e9ee] bg-white hover:bg-[#f5f6f8] text-[#0e1420]"
      >
        <ChevronLeft size={14} strokeWidth={2} />
      </button>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="l10-week-dropdown-btn"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-[#e7e9ee] bg-white text-[#0e1420] hover:bg-[#f5f6f8] min-w-[210px] justify-between"
        >
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays size={13} strokeWidth={1.8} />
            <span className="font-mono-data">{formatWeekLabel(currentIso, todayMon)}</span>
          </span>
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        {open && (
          <div
            role="menu"
            className="absolute right-0 mt-1 z-40 bg-white border border-[#e7e9ee] rounded-md shadow-lg max-h-72 overflow-y-auto min-w-[240px]"
            data-testid="l10-week-dropdown-menu"
          >
            {options.map((o) => (
              <button
                key={o.value}
                onClick={() => pick(o.value)}
                data-testid={`l10-week-option-${o.value}`}
                className={`w-full text-left text-[12.5px] px-3 py-1.5 hover:bg-[#f0f7fc] ${
                  o.value === currentIso
                    ? "bg-[#e7f4fb] text-[#0e1420] font-semibold"
                    : "text-[#0e1420]"
                }`}
              >
                <span className="font-mono-data">{o.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        onClick={goNext}
        disabled={nextDisabled}
        aria-label="Next week"
        data-testid="l10-week-next"
        className={`p-1.5 rounded border ${
          nextDisabled
            ? "border-[#eef1f6] bg-[#f5f6f8] text-[#c5cbd5] cursor-not-allowed"
            : "border-[#e7e9ee] bg-white hover:bg-[#f5f6f8] text-[#0e1420]"
        }`}
      >
        <ChevronRight size={14} strokeWidth={2} />
      </button>
      {!isOnCurrentWeek && (
        <button
          onClick={goThis}
          data-testid="l10-week-this"
          className="ml-1 text-[10.5px] font-semibold uppercase tracking-wider px-2 py-1 rounded bg-[#0072bc] text-white hover:bg-[#005c99]"
        >
          This week
        </button>
      )}
    </div>
  );
}

// ================= Segue =================
function SegueBody({ segue, weekRange }) {
  if (!segue) return <div className="text-[color:var(--cc-ink-muted)] text-[13px]">Loading…</div>;
  // Segue cards. "HIGHEST CLOSE RATE" ships null pending call-center
  // ingest (Broccoli AI) — backend now returns `highest_close: null` with
  // a `highest_close_status: "pending_call_center"` sibling. Render an
  // explicit N/A pill for that card rather than "Nobody / 0%" so no one
  // reads a fabricated close-rate winner.
  //
  // Provenance clarification (2026-07-28 per Brett): when Claire's
  // `tech_performance` payload is present, `highest_close_status` flips
  // to `"from_vps_snapshot"` and the value is a **field close rate** —
  // techs converting their own dispatched jobs, extracted from
  // ServiceTitan's Big-Board PDF. This is **distinct** from call-center
  // close rate (outbound follow-up conversion), which still awaits
  // Broccoli AI. The "(Field)" label suffix + "Source: Claire / Big
  // Board PDF" caption below make the distinction explicit so nobody
  // mistakes this for the call-center metric later.
  const closeRatePending =
    segue.highest_close == null || segue.highest_close_status === "pending_call_center";
  const highestCloseFromVps = segue.highest_close_status === "from_vps_snapshot";
  const cards = [
    { label: "TOP REVENUE TECH", data: segue.top_revenue, format: (v) => fmt$(v.value), footer: weekRange, accent: "#0072bc" },
    {
      label: highestCloseFromVps ? "HIGHEST CLOSE RATE (FIELD)" : "HIGHEST CLOSE RATE",
      data: closeRatePending ? null : segue.highest_close,
      format: (v) => `${v.value}%`,
      footer: closeRatePending
        ? "N/A — call-center ingest (Broccoli AI) not live yet"
        : weekRange,
      caption: highestCloseFromVps ? "Source: Claire / Big Board PDF" : null,
      accent: "#FFC72C",
      pendingLabel: closeRatePending ? "N/A" : null,
    },
    { label: "BEST AVG TICKET", data: segue.best_avg_ticket, format: (v) => fmt$(v.value), footer: "Avg ticket this week", accent: "#166534" },
  ];
  return (
    <div className="grid md:grid-cols-3 gap-3 mt-3">
      {cards.map((c) => (
        <div key={c.label}
             className="bg-white border border-[#e7e9ee] rounded-lg p-4 border-t-4 shadow-sm"
             style={{ borderTopColor: c.accent }}
             data-testid={`l10-segue-${c.label.toLowerCase().replace(/[ ()]/g, '-').replace(/-+/g, '-').replace(/-$/, '')}`}>
          <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-bold">{c.label}</div>
          {c.data ? (
            <>
              <div className="font-display font-semibold text-[16px] text-[#0e1420] mt-2">{c.data.tech}</div>
              <div className="font-mono-data font-bold text-[28px] text-[#0e1420] mt-1 leading-none">{c.format(c.data)}</div>
              {c.caption && (
                <div
                  className="inline-block mt-2 px-1.5 py-0.5 rounded text-[9.5px] font-semibold uppercase tracking-wider bg-[#fff7d6] text-[#8a6100] border border-[#f0dfa2]"
                  data-testid="l10-segue-highest-close-source-pill"
                  title="AI-extracted from ServiceTitan's Big Board PDF by Claude. Field close rate (tech converting his own dispatched job) — NOT call-center close rate."
                >
                  {c.caption}
                </div>
              )}
              <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-2">{c.footer}</div>
            </>
          ) : c.pendingLabel ? (
            <>
              <div className="font-mono-data font-bold text-[28px] text-[#5d6a80] mt-2 leading-none">{c.pendingLabel}</div>
              <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-2">{c.footer}</div>
            </>
          ) : (
            <div className="text-[13px] text-[color:var(--cc-ink-muted)] mt-3">No data</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ================= VPS Snapshot Strip =================
// Renders Claire's `l10_scorecards` row for the current week next to the
// AUTO-computed totals from ScorecardBody. Explicitly shows "VPS said X /
// computed says Y" to make disagreements observable during pilot.
function VpsSnapshotStrip({ vps, computed, onRefresh }) {
  const snap = vps?.snapshot;
  const weekOf = vps?.week_of;
  const [refreshing, setRefreshing] = useState(false);

  const doRefresh = async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    try { await onRefresh(); } finally { setRefreshing(false); }
  };

  // Computed-side totals mirror what ScorecardBody / _compute_scorecard produce.
  // Read them off `computed.rows` where possible; fall back to top-level keys.
  const computedTotals = (() => {
    const revRow = computed?.rows?.find((r) => r.key === "revenue_all_units");
    const closeRow = computed?.rows?.find((r) => r.key === "close_rate");
    const convRow = computed?.rows?.find((r) => r.key === "conversion_rate");
    return {
      total_revenue: revRow?.actual ?? computed?.total_revenue ?? null,
      close_rate: closeRow?.actual ?? computed?.close_rate ?? null,
      conversion_rate: convRow?.actual ?? computed?.conversion_rate ?? null,
      avg_ticket: computed?.job_avg ?? computed?.avg_ticket ?? null,
      tech_count: Array.isArray(computed?.tech_rows) ? computed.tech_rows.length : null,
    };
  })();

  const techCountVps = Array.isArray(snap?.tech_performance)
    ? snap.tech_performance.length : null;

  return (
    <div
      className="border border-[#e7e9ee] rounded-lg bg-[#fbfcfe] mt-3"
      data-testid="l10-vps-snapshot"
    >
      <div className="px-4 py-2.5 border-b border-[#eef1f6] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Radio size={14} strokeWidth={1.8} className="text-[#0072bc]" />
          <span className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
            Weekly Snapshot · Claire
          </span>
          {snap ? (
            <span
              className="text-[10.5px] font-mono-data text-[#186a3b] bg-[#e6f4ea] border border-[#c6e5cf] rounded px-1.5 py-0.5"
              data-testid="l10-vps-snapshot-status"
              title={snap.source_run_id ? `run: ${snap.source_run_id}` : undefined}
            >
              LIVE
            </span>
          ) : (
            <span
              className="text-[10.5px] font-mono-data text-[#8a5b00] bg-[#fff4d6] border border-[#f3d97a] rounded px-1.5 py-0.5"
              data-testid="l10-vps-snapshot-status"
            >
              NO POST YET
            </span>
          )}
        </div>
        <button
          onClick={doRefresh}
          disabled={refreshing}
          data-testid="l10-vps-snapshot-refresh"
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[color:var(--cc-ink-muted)] hover:text-[#0e1420] disabled:opacity-40"
        >
          <RefreshCw size={11} strokeWidth={1.8} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {!snap && (
        <div className="px-4 py-4 text-[12.5px] text-[color:var(--cc-ink-muted)]" data-testid="l10-vps-snapshot-empty">
          No Claire snapshot posted for week of {weekOf || "—"} yet. Values below reflect
          the AUTO-computed aggregate from <span className="font-mono-data">kpi_jobs</span> only.
        </div>
      )}

      {snap && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-[#eef1f6] border-b border-[#eef1f6]">
            <VpsCell
              label="Total Revenue"
              vps={snap.total_revenue}
              computed={computedTotals.total_revenue}
              fmt={fmt$}
              testId="vps-total-revenue"
            />
            <VpsCell
              label="Conversion Rate"
              vps={snap.conversion_rate}
              computed={computedTotals.conversion_rate}
              fmt={(v) => `${Number(v).toFixed(2)}%`}
              testId="vps-conversion-rate"
            />
            <VpsCell
              label="Close Rate"
              vps={snap.close_rate}
              computed={computedTotals.close_rate}
              fmt={(v) => `${Number(v).toFixed(2)}%`}
              testId="vps-close-rate"
              pendingCallCenter
              provenance="Source: ST Big Board PDF"
              provenanceTitle="AI-extracted from ServiceTitan's Big Board PDF by Claude. Averaged across techs with a value present. Not a direct estimates_sold/estimates_presented computation — depends on how ServiceTitan itself computes the value shown in the PDF."
            />
            <VpsCell
              label="Avg Ticket"
              vps={snap.avg_ticket}
              computed={computedTotals.avg_ticket}
              fmt={fmt$}
              testId="vps-avg-ticket"
            />
            <VpsCell
              label="Techs"
              vps={techCountVps}
              computed={computedTotals.tech_count}
              fmt={(v) => String(v)}
              testId="vps-tech-count"
            />
          </div>
          <div className="px-4 py-2 flex items-center justify-between text-[10.5px] text-[color:var(--cc-ink-muted)]">
            <div className="flex items-center gap-2 flex-wrap">
              <span>Week of <span className="font-mono-data text-[#0e1420]">{snap.week_of}</span></span>
              {snap.source_run_id && (
                <span>· run <span className="font-mono-data text-[#0e1420]">{snap.source_run_id}</span></span>
              )}
              {snap.external_id && (
                <span>· external_id <span className="font-mono-data text-[#0e1420]">{snap.external_id}</span></span>
              )}
            </div>
            {snap.updated_at && (
              <span title={snap.updated_at}>
                Posted {new Date(snap.updated_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Single cell: two stacked numbers (VPS on top, computed below) with a small
// disagreement flag when both are non-null and diverge >5% (revenue/ticket) or
// >2pp (rates). Purely visual — no math is done for a "correct" value.
//
// `pendingCallCenter` (2026-07-24 per Brett): for metrics whose "computed"
// side is intentionally null because the underlying real ingest hasn't
// shipped yet (currently only Close Rate — waiting on the ServiceTitan
// call-center API integration in Task 5 backlog), render "N/A pending
// call-center ingest" on the KPI row instead of a dash. Claire's Big
// Board side still renders her PDF-extracted value when present, since
// that's real ST-derived data — just less granular than the eventual
// call-center feed.
function VpsCell({ label, vps, computed, fmt, testId, provenance, provenanceTitle, pendingCallCenter }) {
  const bothPresent = vps != null && computed != null;
  const isRate = /rate|%/.test(label.toLowerCase()) ||
                 (typeof fmt === "function" && fmt.toString().includes("%"));
  const disagreement = bothPresent && !pendingCallCenter && (() => {
    if (isRate) return Math.abs(Number(vps) - Number(computed)) > 2;
    if (label === "Techs") return Number(vps) !== Number(computed);
    // dollar-ish: >5% delta or >$500 absolute
    const delta = Math.abs(Number(vps) - Number(computed));
    return delta > Math.max(500, Number(computed) * 0.05);
  })();

  return (
    <div className="px-4 py-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
        {label}
      </div>
      <div className="mt-1.5 flex flex-col gap-0.5">
        <div className="flex items-baseline gap-1.5">
          <span className="text-[10px] font-mono-data text-[#0072bc] uppercase tracking-widest">CLAIRE</span>
          <span className="text-[14.5px] font-mono-data font-semibold text-[#0e1420]" data-testid={`${testId}-vps`}>
            {vps == null || (pendingCallCenter && Number(vps) === 0) ? (
              pendingCallCenter ? (
                <span className="italic text-[#5d6a80]" title="ServiceTitan Big Board PDF extraction returned no value for this week.">N/A</span>
              ) : "—"
            ) : fmt(vps)}
          </span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[10px] font-mono-data text-[color:var(--cc-ink-muted)] uppercase tracking-widest">Kpi</span>
          <span className="text-[13px] font-mono-data text-[color:var(--cc-ink-muted)]" data-testid={`${testId}-computed`}>
            {pendingCallCenter ? (
              <span className="italic" title="Real close rate requires ServiceTitan call-center ingest, which isn't wired yet.">N/A · call-center ingest pending</span>
            ) : computed == null ? "—" : fmt(computed)}
          </span>
          {disagreement && (
            <span
              className="text-[9px] font-semibold text-[#a12b2b] bg-[#fdecec] border border-[#f5c5c5] rounded px-1 py-0.5 uppercase tracking-widest"
              data-testid={`${testId}-disagreement`}
              title="Claire snapshot and Agent 12 kpi_jobs aggregate disagree materially"
            >
              Δ
            </span>
          )}
        </div>
        {provenance && (
          <span
            className="mt-1 self-start text-[9.5px] font-mono-data uppercase tracking-widest border rounded px-1.5 py-0.5 bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]"
            data-testid={`${testId}-provenance`}
            title={provenanceTitle || provenance}
          >
            {provenance}
          </span>
        )}
      </div>
    </div>
  );
}


// ================= Scorecard =================
function ScorecardBody({ scorecard, manual, onManualSave }) {
  const saveField = (field, raw) => {
    const parsed = raw === "" || raw === null ? null : Number(raw);
    if (Number.isNaN(parsed)) return;
    onManualSave({ [field]: parsed });
  };
  const rows = scorecard.rows || [];
  return (
    <div className="border border-[#e7e9ee] rounded-lg overflow-x-auto mt-3">
      <table className="w-full min-w-[720px] text-[13.5px]">
        <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
          <tr>
            <th className="text-left py-2.5 px-4 w-[36%]">Metric</th>
            <th className="text-left py-2.5 px-4 w-[16%]">Goal</th>
            <th className="text-left py-2.5 px-4 w-[18%]" data-testid="l10-scorecard-week-col-header">
              {(() => {
                const w = scorecard.week_of;
                if (!w) return "This Week";
                const d = new Date(w + "T00:00:00");
                const end = new Date(d.getTime() + 6 * 86400000);
                const fmt = (x) => x.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
                return `Week of ${fmt(d)}`;
              })()}
            </th>
            <th className="text-left py-2.5 px-4">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isManual = r.meta?.manual;
            const field = ({
              broadly_5star: "broadly_5star",
              broadly_1_2star: "broadly_1_2star",
              nps_score: "nps_score",
              ar_aging: "ar_aging_days",
            })[r.key];
            return (
              <tr key={r.key} className="border-t border-[#eef1f6]" data-testid={`l10-metric-${r.key}`}>
                <td className="py-2.5 px-4 font-semibold text-[#0e1420]">
                  {r.label}
                  {r.meta?.source === "vps_snapshot" && (
                    <span
                      className="ml-2 inline-block px-1.5 py-0.5 rounded text-[9.5px] font-semibold uppercase tracking-wider bg-[#fff7d6] text-[#8a6100] border border-[#f0dfa2] align-middle"
                      data-testid={`l10-metric-source-pill-${r.key}`}
                      title="AI-extracted from ServiceTitan's Big Board PDF by Claude. Field close rate (tech converting his own dispatched job) — NOT call-center close rate. Call-center metric is pending Broccoli AI."
                    >
                      Source: Claire
                    </span>
                  )}
                </td>
                <td className="py-2.5 px-4 font-mono-data text-[color:var(--cc-ink-muted)]">{r.goal}</td>
                <td className="py-2.5 px-4 font-mono-data font-semibold text-[#0e1420]">
                  {isManual && field ? (
                    <input
                      type="number"
                      defaultValue={r.this_week ?? ""}
                      placeholder={r.meta?.unit === "d" ? "days" : (r.meta?.unit || "count")}
                      onBlur={(e) => { if (e.target.value !== String(r.this_week ?? "")) saveField(field, e.target.value); }}
                      data-testid={`l10-manual-${r.key}`}
                      className="bg-white border border-[#e7e9ee] rounded px-2 py-1 w-28 font-mono-data text-[#0e1420] focus:border-[#0072bc] outline-none"
                    />
                  ) : (
                    <>{r.meta?.value_label || fmtN(r.this_week, r.meta?.unit)}</>
                  )}
                </td>
                <td className="py-2.5 px-4">
                  <div className="flex flex-wrap items-center gap-1">
                    <StatusBadge status={r.status} />
                    <PersistentTag weeks={r.meta?.persistent_weeks} />
                  </div>
                  {r.meta?.detail && (
                    <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-1">{r.meta.detail}</div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TechPerformance({ techRows, weekEnding, snapshotUpdatedAt, snapshotStatus }) {
  // 2026-08-14 (per Brett post-close-rate-rewire): Since Close Rate
  // now depends on Claire's weekly VPS snapshot, ops needs a visible
  // freshness signal — otherwise N/A on a tech is ambiguous ("snapshot
  // hasn't been posted" vs "posted but this tech's row is missing")
  // AND stale numbers on a Monday-meeting screen look identical to
  // fresh ones. Rendered as a small caption to the right of the
  // section title, color-coded:
  //   - status="present" AND updated <=24h → dim gray (silent good)
  //   - status="present" AND updated 24-72h → amber ("a day or two")
  //   - status="present" AND updated >72h → red ("multiple days stale")
  //   - status="empty" → amber ("snapshot posted but tech rows blank")
  //   - status="missing" → red ("no VPS snapshot for this week")
  const now = Date.now();
  const parsed = snapshotUpdatedAt ? new Date(snapshotUpdatedAt).getTime() : null;
  const ageMs = parsed && !Number.isNaN(parsed) ? now - parsed : null;
  const ageHours = ageMs != null ? ageMs / 36e5 : null;
  const rel = (() => {
    if (ageHours == null) return null;
    if (ageHours < 1) return `${Math.max(1, Math.round(ageHours * 60))}m ago`;
    if (ageHours < 24) return `${Math.round(ageHours)}h ago`;
    const days = Math.round(ageHours / 24);
    return `${days}d ago`;
  })();
  let captionText, captionTone, captionTitle;
  if (snapshotStatus === "missing" || !snapshotUpdatedAt) {
    captionText = "No VPS snapshot for this week";
    captionTone = "text-[#8a1414] bg-[#fde7e7] border-[#f0a2a2]";
    captionTitle = "Claire hasn't posted a scorecard for this week yet. Every close rate will show N/A until she does.";
  } else if (snapshotStatus === "empty") {
    captionText = `Snapshot posted ${rel} · tech rows empty`;
    captionTone = "text-[#8a5a14] bg-[#fdf2d7] border-[#f0d59f]";
    captionTitle = "The snapshot document exists but its tech_performance array is empty. Ping Claire.";
  } else if (ageHours != null && ageHours > 72) {
    captionText = `Snapshot updated ${rel}`;
    captionTone = "text-[#8a1414] bg-[#fde7e7] border-[#f0a2a2]";
    captionTitle = "Snapshot is more than 3 days old. Close rates may be stale.";
  } else if (ageHours != null && ageHours > 24) {
    captionText = `Snapshot updated ${rel}`;
    captionTone = "text-[#8a5a14] bg-[#fdf2d7] border-[#f0d59f]";
    captionTitle = "Snapshot is a day+ old. Should be fine for a Monday meeting reviewing last week.";
  } else {
    captionText = `Snapshot updated ${rel || "just now"}`;
    captionTone = "text-[#4a5568] bg-[#f5f6f8] border-[#dde3ec]";
    captionTitle = "Fresh — Claire's snapshot was posted within the last 24 hours.";
  }
  return (
    <div className="mt-5">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="w-1 h-4 rounded-sm bg-[#0072bc]" />
        <span className="text-[11px] uppercase tracking-[.09em] text-[#0e1420] font-bold">Tech Performance</span>
        <span className="text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-semibold">· Week ending {weekEnding}</span>
        <span
          className={`text-[10.5px] font-mono-data uppercase tracking-widest border rounded px-2 py-0.5 ${captionTone}`}
          title={captionTitle}
          data-testid="l10-tech-perf-snapshot-freshness"
        >
          {captionText}
        </span>
      </div>
      <div className="border border-[#e7e9ee] rounded-lg overflow-x-auto">
        <table className="w-full min-w-[820px] text-[13.5px]" data-testid="l10-tech-perf">
          <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-bold">
            <tr>
              <th className="text-left py-2.5 px-4">Technician</th>
              <th className="text-right py-2.5 px-4">Revenue</th>
              <th className="text-right py-2.5 px-4">Goal</th>
              <th className="text-right py-2.5 px-4">% of Goal</th>
              <th className="text-right py-2.5 px-4">Close Rate</th>
              <th className="text-right py-2.5 px-4">Jobs</th>
              <th className="text-right py-2.5 px-4">Avg Ticket</th>
              <th className="text-left py-2.5 px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {techRows.map((t) => {
              const pctColor = t.pct_of_goal == null
                ? "text-[color:var(--cc-ink-muted)]"
                : t.pct_of_goal >= 100 ? "text-[#166534]"
                : t.pct_of_goal >= 70 ? "text-[#854d0e]"
                : "text-[#991b1b]";
              return (
                <tr key={t.tech_name} className="border-t border-[#eef1f6]" data-testid={`l10-tech-row-${t.tech_name}`}>
                  <td className="py-2.5 px-4 font-semibold text-[#0e1420]">{t.tech_name}</td>
                  <td className="py-2.5 px-4 text-right font-mono-data font-semibold">{fmt$(t.revenue)}</td>
                  <td className="py-2.5 px-4 text-right font-mono-data text-[color:var(--cc-ink-muted)]">
                    {t.has_goal ? fmt$(t.goal) : <span className="italic text-[#854d0e]">not set</span>}
                  </td>
                  <td className={`py-2.5 px-4 text-right font-mono-data font-bold ${pctColor}`}>
                    {t.pct_of_goal != null ? `${t.pct_of_goal}%` : "—"}
                  </td>
                  <td className="py-2.5 px-4 text-right font-mono-data">
                    {/* 2026-08-14 rewire: Field Close Rate now sourced
                        from Claire's tech_performance[].close_rate VPS
                        snapshot (backend: _compute_scorecard). When the
                        snapshot has a value for this tech, `close_rate`
                        is a real percentage. When missing, backend ships
                        null with either `not_in_snapshot` (snapshot
                        exists, this tech isn't in it) or
                        `pending_snapshot` (no VPS scorecard for the
                        week yet). Retired the old `pending_call_center`
                        gate — call-center close rate is a separate
                        future metric that must not gate the field
                        number Claire delivers weekly. */}
                    {t.close_rate == null ? (
                      <span
                        className="text-[10.5px] text-[#5d6a80] italic"
                        title={
                          t.close_rate_status === "not_in_snapshot"
                            ? "Field close rate — this tech isn't in this week's VPS snapshot."
                            : "Field close rate — waiting on this week's VPS snapshot from Claire."
                        }
                        data-testid={`l10-tech-close-rate-na-${t.tech_name}`}
                      >
                        N/A
                      </span>
                    ) : (
                      `${t.close_rate}%`
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-right font-mono-data text-[color:var(--cc-ink-muted)]">{t.jobs}</td>
                  <td className="py-2.5 px-4 text-right font-mono-data">{fmt$(t.avg_ticket)}</td>
                  <td className="py-2.5 px-4"><StatusBadge status={t.status} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ================= Rocks =================
function RocksBody({ rocks, setRocks }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", owner: "", due_date: "" });

  const toggle = async (r) => {
    const { data } = await api.patch(`/l10/rocks/${r.id}`, { on_track: !r.on_track });
    setRocks((prev) => prev.map((x) => x.id === r.id ? data : x));
  };
  const remove = async (r) => {
    await api.delete(`/l10/rocks/${r.id}`);
    setRocks((prev) => prev.filter((x) => x.id !== r.id));
  };
  const submit = async () => {
    if (!form.title.trim()) return;
    const { data } = await api.post("/l10/rocks", form);
    setRocks((prev) => [...prev, data]);
    setForm({ title: "", owner: "", due_date: "" });
    setAdding(false);
  };

  return (
    <div className="space-y-2 mt-3">
      {rocks.map((r) => (
        <div key={r.id}
             className="flex items-start gap-3 bg-white border border-[#e7e9ee] rounded-lg p-4 hover:border-[#0072bc]/40 transition-colors"
             data-testid={`l10-rock-${r.id}`}>
          <button
            onClick={() => toggle(r)}
            data-testid={`l10-rock-toggle-${r.id}`}
            aria-label={r.on_track ? "Mark off track" : "Mark on track"}
            className={`shrink-0 relative inline-block rounded-full transition-colors ${r.on_track ? "bg-[#0e8a68]" : "bg-[#c73535]"}`}
            style={{ width: "44px", height: "24px" }}
          >
            <span
              className="absolute rounded-full bg-white shadow-sm transition-transform"
              style={{
                width: "18px",
                height: "18px",
                top: "3px",
                left: "3px",
                transform: r.on_track ? "translateX(20px)" : "translateX(0)",
              }}
            />
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[14px] text-[#0e1420]">{r.title}</div>
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5">
              Owner: <span className="font-mono-data">{r.owner || "—"}</span>
              {" · "}Due: <span className="font-mono-data">{r.due_date || "—"}</span>
            </div>
            <div className="mt-1">
              <StatusBadge status={r.on_track ? "ON_TARGET" : "OFF_TRACK"} />
            </div>
          </div>
          <button
            onClick={() => remove(r)}
            data-testid={`l10-rock-remove-${r.id}`}
            aria-label="Remove rock"
            className="text-[color:var(--cc-ink-muted)] hover:text-[#c73535] p-1"
          >
            <X size={16} />
          </button>
        </div>
      ))}
      {adding ? (
        <div className="grid md:grid-cols-4 gap-2 items-center bg-[#fafbfc] border border-[#e7e9ee] rounded-lg p-4">
          <input placeholder="Rock title..." value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                 data-testid="l10-rock-input-title"
                 className="col-span-2 bg-white border border-[#e7e9ee] rounded px-3 py-2 text-[13px] text-[#0e1420] outline-none focus:border-[#0072bc]" />
          <input placeholder="Owner..." value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })}
                 className="bg-white border border-[#e7e9ee] rounded px-3 py-2 text-[13px] text-[#0e1420] outline-none focus:border-[#0072bc]" />
          <div className="flex gap-2">
            <input placeholder="Due (Q3 2026)" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                   className="flex-1 bg-white border border-[#e7e9ee] rounded px-3 py-2 text-[13px] text-[#0e1420] outline-none focus:border-[#0072bc]" />
            <button onClick={submit} data-testid="l10-rock-save"
                    className="text-[12px] font-semibold px-3 py-2 rounded bg-[#0072bc] text-white hover:bg-[#005c99]">Save</button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          data-testid="l10-rock-add"
          className="w-full border border-dashed border-[#c5e0ee] text-[#0072bc] hover:bg-[#eef4fb] rounded-lg py-2.5 text-[12.5px] font-semibold flex items-center justify-center gap-1"
        >
          <Plus size={14} strokeWidth={2} /> Add Rock
        </button>
      )}
    </div>
  );
}

// ================= Headlines =================
function HeadlinesBody({ headlines, setHeadlines, resolvedWeekOf }) {
  const [text, setText] = useState("");
  const submit = async () => {
    if (!text.trim()) return;
    // Headline POST accepts `week_of` in body; pin it to the currently-
    // viewed week so headlines added while browsing a specific week
    // land on that week (not the server's latest_reporting default).
    const { data } = await api.post("/l10/headlines", { text, week_of: resolvedWeekOf });
    setHeadlines((prev) => ({ ...prev, manual: [...prev.manual, data] }));
    setText("");
  };
  const remove = async (id) => {
    await api.delete(`/l10/headlines/${id}`);
    setHeadlines((prev) => ({ ...prev, manual: prev.manual.filter((h) => h.id !== id) }));
  };
  const KIND = {
    goal_achieved:  { label: "GOAL ACHIEVED",       bar: "border-l-[#0e8a68]" },
    revenue_leader: { label: "REVENUE LEADER",      bar: "border-l-[#0072bc]" },
    below_goal:     { label: "BELOW GOAL — HEADS UP", bar: "border-l-[#c73535]" },
    manual:         { label: "TEAM NOTE",           bar: "border-l-[#8a6a1c]" },
  };
  // Provenance parser — resolves the source-of-truth label for each auto-array
  // item so operators can eyeball "computed at request time" vs "agent-posted".
  // Contract with GET /api/l10/headlines (server.py:2575): persisted rows come
  // through with an `id` field; synthesized on-the-fly rows don't. External
  // agent posts are further identified by an `external_id` prefix (e.g.
  // "claire-hl-…" → "CLAIRE"). Anything persisted without a recognizable prefix
  // falls back to a generic EXTERNAL badge; anything without an id is COMPUTED.
  const provenanceFor = (h) => {
    if (!h.id) return { text: "COMPUTED · KPI_JOBS", tone: "bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]" };
    const ext = h.external_id || "";
    const m = ext.match(/^([a-z][a-z0-9_]*)[-:]/i);
    const agent = m ? m[1].toUpperCase() : "AGENT";
    return { text: `EXTERNAL · ${agent}`, tone: "bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]" };
  };
  return (
    <div className="space-y-2.5 mt-3">
      {headlines.auto.map((h, i) => {
        const prov = provenanceFor(h);
        return (
          <div key={h.id || `auto-${i}`}
               className={`border-l-4 ${KIND[h.kind]?.bar || KIND.manual.bar} bg-white border border-[#e7e9ee] rounded-lg p-3.5`}
               data-testid={`l10-headline-${h.kind}`}>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
                {KIND[h.kind]?.label}
              </span>
              <span
                className={`text-[9.5px] font-mono-data uppercase tracking-widest border rounded px-1.5 py-0.5 ${prov.tone}`}
                data-testid={`l10-headline-provenance-${h.kind}`}
                title={h.external_id ? `external_id: ${h.external_id}` : "Synthesized at request time from kpi_jobs"}
              >
                {prov.text}
              </span>
            </div>
            <div className="text-[13.5px] text-[#0e1420] font-semibold">{h.text}</div>
          </div>
        );
      })}
      {headlines.manual.map((h) => (
        <div key={h.id}
             className={`border-l-4 ${KIND.manual.bar} bg-white border border-[#e7e9ee] rounded-lg p-3.5 flex items-start justify-between gap-3`}
             data-testid={`l10-headline-manual-${h.id}`}>
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1">{KIND.manual.label}</div>
            <div className="text-[13.5px] text-[#0e1420]">{h.text}</div>
          </div>
          <button onClick={() => remove(h.id)} aria-label="Remove headline"
                  className="text-[color:var(--cc-ink-muted)] hover:text-[#c73535] p-1"><X size={14} /></button>
        </div>
      ))}
      <div className="pt-3 border-t border-[#eef1f6] mt-2">
        <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">
          Add headline data can&apos;t capture (Missie)
        </div>
        <div className="flex gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder="e.g. customer shoutout, team milestone, employee kudos..."
            data-testid="l10-headline-textarea"
            className="flex-1 bg-white border border-[#e7e9ee] rounded px-3 py-2 text-[13px] text-[#0e1420] outline-none focus:border-[#0072bc] resize-none"
          />
          <button onClick={submit} data-testid="l10-headline-add"
                  className="text-[12px] font-semibold px-3 rounded bg-[#0072bc] text-white hover:bg-[#005c99] whitespace-nowrap">
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

// ================= Todos =================
function TodosBody({ todos, setTodos, stagedTodos = [], setStagedTodos = () => {} }) {
  const [form, setForm] = useState({ action: "", owner: "", due_date: "" });
  // Per-row inline owner input for staged rows whose owner is null.
  // Keyed by row index; cleared when the row is added/removed.
  const [ownerDrafts, setOwnerDrafts] = useState({});
  const setOwnerDraft = (i, v) => setOwnerDrafts((p) => ({ ...p, [i]: v }));

  // 2026-08-14: inline edit control for existing todos (action + owner
  // + due_date). Only one row can be in edit mode at a time — keeps
  // the UI clean and prevents accidental double-saves. `editingId=null`
  // means no row edit is active. Draft state lives here rather than
  // on each row so a save failure keeps the user's typing visible
  // for retry.
  //
  // 2026-08-15: added `action` (per Brett — delete+re-add just to fix a
  // typo defeated the purpose). Action is the primary content of the
  // row, so it gets a wider input than owner/due-date. Empty action
  // is rejected client-side (guard in saveEdit) to match the create
  // form's `if (!form.action.trim()) return`.
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ action: "", owner: "", due_date: "" });
  const startEdit = (t) => {
    setEditingId(t.id);
    setEditDraft({
      action: t.action || "",
      owner: t.owner || "",
      due_date: t.due_date || "",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ action: "", owner: "", due_date: "" });
  };
  const saveEdit = async (t) => {
    const nextAction = editDraft.action.trim();
    if (!nextAction) return;  // silent no-op — matches create-form UX
    const patch = {
      action: nextAction,
      owner: editDraft.owner || null,
      due_date: editDraft.due_date || null,
    };
    const { data } = await api.patch(`/l10/todos/${t.id}`, patch);
    setTodos((prev) => prev.map((x) => (x.id === t.id ? data : x)));
    cancelEdit();
  };

  const toggle = async (t) => {
    const { data } = await api.patch(`/l10/todos/${t.id}`, { completed: !t.completed });
    setTodos((prev) => prev.map((x) => x.id === t.id ? data : x));
  };
  const remove = async (t) => {
    await api.delete(`/l10/todos/${t.id}`);
    setTodos((prev) => prev.filter((x) => x.id !== t.id));
  };
  const submit = async () => {
    if (!form.action.trim()) return;
    const { data } = await api.post("/l10/todos", form);
    setTodos((prev) => [...prev, data]);
    setForm({ action: "", owner: "", due_date: "" });
  };

  // Staged-row helpers (client-only until user clicks Add).
  const stagedOwnerFor = (row, i) => (row.owner ?? ownerDrafts[i] ?? "").trim();
  const rowIsAddable = (row, i) => Boolean(stagedOwnerFor(row, i));

  // 2026-08-14: possible-duplicate detection at import time. Matches the
  // staged row against the current OPEN todos list on normalized action
  // text + normalized owner. Flag-only (per user: "a visual flag so I
  // can manually dedupe is enough for now"). Auto-merge is intentionally
  // NOT implemented — a duplicate flag on a staged row does not block
  // Add, it just paints a badge so ops can eyeball before committing.
  const normalize = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();
  const isPossibleDup = (row, i) => {
    const rowAction = normalize(row.action);
    if (!rowAction) return false;
    const rowOwner = normalize(stagedOwnerFor(row, i));
    return todos.some(
      (t) => !t.completed
        && normalize(t.action) === rowAction
        && (rowOwner === "" || normalize(t.owner) === rowOwner),
    );
  };

  const addStagedRow = async (row, i) => {
    const owner = stagedOwnerFor(row, i);
    if (!owner) return; // guard — button is disabled but be defensive
    const payload = { action: row.action, owner, due_date: row.due_date };
    const { data } = await api.post("/l10/todos", payload);
    setTodos((prev) => [...prev, data]);
    setStagedTodos((prev) => prev.filter((_, idx) => idx !== i));
    setOwnerDrafts((p) => {
      const c = { ...p }; delete c[i]; return c;
    });
  };

  const addAll = async () => {
    // Only add rows that have a resolved owner. Skipped rows stay in the preview.
    const addable = stagedTodos.map((r, i) => ({ r, i })).filter(({ r, i }) => rowIsAddable(r, i));
    const skipped = stagedTodos.length - addable.length;
    if (addable.length === 0) return;
    const created = [];
    for (const { r, i } of addable) {
      const payload = { action: r.action, owner: stagedOwnerFor(r, i), due_date: r.due_date };
      const { data } = await api.post("/l10/todos", payload);
      created.push(data);
    }
    setTodos((prev) => [...prev, ...created]);
    setStagedTodos((prev) => prev.filter((r, i) => !rowIsAddable(r, i)));
    setOwnerDrafts({});
    if (skipped > 0) {
      console.info(`[l10] Added ${created.length}, skipped ${skipped} (needs owner).`);
    }
  };

  const discardAll = () => {
    // Explicit confirm — the whole workflow rests on nothing disappearing silently.
    if (!window.confirm(`Discard all ${stagedTodos.length} staged todos? They will not be saved.`)) return;
    setStagedTodos([]);
    setOwnerDrafts({});
  };

  return (
    <div className="space-y-1 mt-3">
      {/* Import preview strip — additive, only renders when stagedTodos has rows */}
      {stagedTodos.length > 0 && (
        <div className="mb-4 p-3 bg-[#fffbeb] border border-[#f0dfa2] rounded-lg"
             data-testid="l10-todo-import-preview">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div>
              <div className="text-[11.5px] font-semibold text-[#8a6100] uppercase tracking-wider">
                Import preview
              </div>
              <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-0.5">
                {stagedTodos.length} todo{stagedTodos.length === 1 ? "" : "s"} from Claude transcript extract — review before adding.
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={addAll}
                data-testid="l10-todo-import-add-all"
                className="text-[11.5px] font-semibold px-3 py-1.5 rounded bg-[#0e8a68] text-white hover:bg-[#0b6e53]"
              >
                Add all
              </button>
              <button
                onClick={discardAll}
                data-testid="l10-todo-import-discard"
                className="text-[11.5px] font-semibold px-3 py-1.5 rounded border border-[#e7e9ee] text-[#0e1420] hover:bg-white"
              >
                Discard
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {stagedTodos.map((row, i) => {
              const needsOwner = !row.owner;
              const addable = rowIsAddable(row, i);
              const possibleDup = isPossibleDup(row, i);
              return (
                <div key={i}
                     className="flex flex-wrap items-center gap-2 py-1.5 border-t border-[#f0dfa2] first:border-t-0"
                     data-testid={`l10-todo-import-row-${i}`}>
                  <div className="flex-1 min-w-[220px] text-[12.5px] text-[#0e1420] flex items-center gap-2">
                    <span>{row.action}</span>
                    {possibleDup && (
                      <span
                        className="text-[9.5px] font-semibold uppercase tracking-wider bg-[#fde7e7] text-[#8a1414] border border-[#f0a2a2] rounded px-1.5 py-0.5"
                        data-testid={`l10-todo-import-dup-${i}`}
                        title="An open todo with the same action + owner already exists — review before adding."
                      >
                        Possible dup
                      </span>
                    )}
                  </div>
                  {needsOwner ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9.5px] font-semibold uppercase tracking-wider bg-[#fff7d6] text-[#8a6100] border border-[#f0dfa2] rounded px-1.5 py-0.5"
                            data-testid={`l10-todo-import-needs-owner-${i}`}>
                        Needs owner
                      </span>
                      <input
                        type="text"
                        placeholder="Owner..."
                        value={ownerDrafts[i] || ""}
                        onChange={(e) => setOwnerDraft(i, e.target.value)}
                        data-testid={`l10-todo-import-owner-input-${i}`}
                        className="w-32 bg-white border border-[#e7e9ee] rounded px-2 py-1 text-[12px] text-[#0e1420] outline-none focus:border-[#0072bc]"
                      />
                    </div>
                  ) : (
                    <span className="text-[10.5px] font-mono-data uppercase tracking-widest bg-[#e7f4fb] text-[#1c6a99] border border-[#c5e0ee] rounded px-2 py-0.5">
                      {row.owner}
                    </span>
                  )}
                  <span className="text-[11px] font-mono-data text-[color:var(--cc-ink-muted)] w-24 text-right">
                    {row.due_date || "—"}
                  </span>
                  <button
                    onClick={() => addStagedRow(row, i)}
                    disabled={!addable}
                    data-testid={`l10-todo-import-add-${i}`}
                    className={`text-[11.5px] font-semibold px-2.5 py-1 rounded ${
                      addable
                        ? "bg-[#0072bc] text-white hover:bg-[#005c99]"
                        : "bg-[#e7e9ee] text-[#8a95a8] cursor-not-allowed"
                    }`}
                  >
                    Add
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {todos.map((t) => {
        const isEditing = editingId === t.id;
        return (
        <div key={t.id} className="flex items-center gap-3 py-1.5 border-b border-[#eef1f6] last:border-b-0"
             data-testid={`l10-todo-${t.id}`}>
          <button onClick={() => toggle(t)} className="shrink-0" data-testid={`l10-todo-toggle-${t.id}`}
                  aria-label={t.completed ? "Mark not done" : "Mark done"}>
            {t.completed
              ? <CheckCircle2 size={18} strokeWidth={1.9} className="text-[#0e8a68]" />
              : <Circle size={18} strokeWidth={1.6} className="text-[color:var(--cc-ink-muted)]" />}
          </button>
          <div className={`flex-1 text-[13.5px] ${t.completed ? "line-through text-[color:var(--cc-ink-muted)]" : "text-[#0e1420]"}`}>
            {isEditing ? (
              <input
                type="text"
                placeholder="Action / task..."
                value={editDraft.action}
                onChange={(e) => setEditDraft((d) => ({ ...d, action: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") saveEdit(t); if (e.key === "Escape") cancelEdit(); }}
                data-testid={`l10-todo-edit-action-${t.id}`}
                autoFocus
                className="w-full bg-white border border-[#0072bc] rounded px-2 py-1 text-[13.5px] text-[#0e1420] outline-none"
              />
            ) : (
              t.action
            )}
          </div>
          {isEditing ? (
            <>
              <input
                type="text"
                placeholder="Owner..."
                value={editDraft.owner}
                onChange={(e) => setEditDraft((d) => ({ ...d, owner: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") saveEdit(t); if (e.key === "Escape") cancelEdit(); }}
                data-testid={`l10-todo-edit-owner-${t.id}`}
                className="w-28 bg-white border border-[#0072bc] rounded px-2 py-1 text-[12px] text-[#0e1420] outline-none"
              />
              <input
                type="date"
                value={editDraft.due_date}
                onChange={(e) => setEditDraft((d) => ({ ...d, due_date: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") saveEdit(t); if (e.key === "Escape") cancelEdit(); }}
                data-testid={`l10-todo-edit-due-${t.id}`}
                className="w-36 bg-white border border-[#0072bc] rounded px-2 py-1 text-[12px] text-[#0e1420] outline-none"
              />
              <button
                onClick={() => saveEdit(t)}
                data-testid={`l10-todo-edit-save-${t.id}`}
                className="text-[11px] font-semibold px-2 py-1 rounded bg-[#0e8a68] text-white hover:bg-[#0b6e53]"
              >
                Save
              </button>
              <button
                onClick={cancelEdit}
                data-testid={`l10-todo-edit-cancel-${t.id}`}
                className="text-[11px] font-semibold px-2 py-1 rounded border border-[#e7e9ee] text-[#0e1420] hover:bg-white"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {t.owner && (
                <span className="text-[10.5px] font-mono-data uppercase tracking-widest bg-[#e7f4fb] text-[#1c6a99] border border-[#c5e0ee] rounded px-2 py-0.5">
                  {t.owner}
                </span>
              )}
              {t.due_date && <span className="text-[11px] font-mono-data text-[color:var(--cc-ink-muted)]">{t.due_date}</span>}
              <button onClick={() => startEdit(t)} aria-label="Edit todo"
                      data-testid={`l10-todo-edit-${t.id}`}
                      className="text-[color:var(--cc-ink-muted)] hover:text-[#0072bc] p-1"><Pencil size={13} /></button>
              <button onClick={() => remove(t)} aria-label="Remove todo"
                      className="text-[color:var(--cc-ink-muted)] hover:text-[#c73535] p-1"><X size={14} /></button>
            </>
          )}
        </div>
      );
      })}
      <div className="flex flex-wrap gap-2 pt-3 mt-2">
        <input placeholder="Action item..." value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })}
               data-testid="l10-todo-input-action"
               className="flex-1 min-w-[180px] bg-white border border-[#e7e9ee] rounded px-3 py-2 text-[13px] text-[#0e1420] outline-none focus:border-[#0072bc]" />
        <input placeholder="Owner..." value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })}
               className="w-32 bg-white border border-[#e7e9ee] rounded px-3 py-2 text-[13px] text-[#0e1420] outline-none focus:border-[#0072bc]" />
        <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
               className="w-36 bg-white border border-[#e7e9ee] rounded px-3 py-2 text-[13px] text-[#0e1420] outline-none focus:border-[#0072bc]" />
        <button onClick={submit} data-testid="l10-todo-add"
                className="text-[12px] font-semibold px-3 rounded bg-[#0072bc] text-white hover:bg-[#005c99]">
          + Add
        </button>
      </div>
    </div>
  );
}

// ================= IDS =================
function IdsBody({ ids, setIds, reload }) {
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});
  const setDraft = (id, val) => setDrafts((p) => ({ ...p, [id]: val }));

  // 2026-08-14: inline owner edit — the auto-generated `suggested_owner`
  // is a heuristic (see server.py:_suggest_owner) and is wrong often
  // enough that ops needs a way to correct it without dropping to curl.
  // Same one-row-at-a-time pattern as the Todos edit control.
  const [editingOwnerId, setEditingOwnerId] = useState(null);
  const [ownerDraft, setOwnerDraft] = useState("");
  const startOwnerEdit = (iss) => {
    setEditingOwnerId(iss.id);
    setOwnerDraft(iss.suggested_owner || "");
  };
  const cancelOwnerEdit = () => {
    setEditingOwnerId(null);
    setOwnerDraft("");
  };
  const saveOwnerEdit = async (iss) => {
    const { data } = await api.patch(`/l10/ids/${iss.id}`, {
      suggested_owner: ownerDraft || null,
    });
    setIds((prev) => prev.map((x) => (x.id === iss.id ? data : x)));
    cancelOwnerEdit();
  };

  // 2026-08-14: dismiss/resolve control. Backend DELETE is soft
  // (dismissed=true + dismissed_at); the row stays in DB for audit
  // and is filtered from `GET /l10/ids`. Per-week scoped, so if the
  // same metric goes off-track next reporting week, a NEW auto-issue
  // will be generated — dismissal closes THIS week's conversation
  // only, not the underlying scorecard rule.
  const dismiss = async (iss) => {
    if (!window.confirm(`Dismiss this issue for the current week?\n\n"${iss.title}"\n\nIt will be hidden from the L10 list. If the underlying metric stays off-track, it will re-fire NEXT reporting week.`)) return;
    setBusy((p) => ({ ...p, [iss.id]: true }));
    await api.delete(`/l10/ids/${iss.id}`);
    setIds((prev) => prev.filter((x) => x.id !== iss.id));
    setBusy((p) => ({ ...p, [iss.id]: false }));
  };

  // 2026-08-14: intra-list duplicate flag — highlight rows that share
  // a metric_key with another row currently in the list. Rare but real:
  // Claire's ingest occasionally re-fires an IDS issue for the same
  // metric_key on a slightly different title (e.g. Q3 close_rate vs
  // Q3 field close_rate) so the same underlying scorecard signal
  // appears twice. Visual-only flag, no auto-merge (per user).
  const dupMetrics = new Set();
  const seen = new Set();
  for (const iss of ids) {
    if (!iss.metric_key) continue;
    if (seen.has(iss.metric_key)) dupMetrics.add(iss.metric_key);
    seen.add(iss.metric_key);
  }

  const promote = async (issue) => {
    const text = (drafts[issue.id] ?? issue.resolution ?? "").trim();
    if (!text) return;
    setBusy((p) => ({ ...p, [issue.id]: true }));
    await api.patch(`/l10/ids/${issue.id}`, { resolution: text });
    await api.post(`/l10/ids/${issue.id}/promote-to-todo`);
    setIds((prev) => prev.map((x) => x.id === issue.id ? { ...x, resolution: text, promoted_to_todo_id: "pending" } : x));
    setBusy((p) => ({ ...p, [issue.id]: false }));
    reload();
  };

  return (
    <div className="space-y-3 mt-3">
      {ids.length === 0 && (
        <div className="text-[13px] text-[color:var(--cc-ink-muted)] italic py-2">
          No issues auto-generated this week. Scorecard is clean.
        </div>
      )}
      {ids.map((iss) => {
        const isDup = iss.metric_key && dupMetrics.has(iss.metric_key);
        const isEditingOwner = editingOwnerId === iss.id;
        return (
        <div key={iss.id}
             className="border-l-4 border-l-[#c73535] bg-white border border-[#e7e9ee] rounded-lg p-4"
             data-testid={`l10-ids-${iss.id}`}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={13} strokeWidth={2} className="text-[#c73535]" />
            <div className="text-[10.5px] uppercase tracking-widest text-[#c73535] font-semibold">
              {iss.title.split("—")[0].trim()} — {iss.metric_key || "MANUAL"}
            </div>
            {isDup && (
              <span
                className="text-[9.5px] font-semibold uppercase tracking-wider bg-[#fde7e7] text-[#8a1414] border border-[#f0a2a2] rounded px-1.5 py-0.5"
                data-testid={`l10-ids-dup-${iss.id}`}
                title="Another issue on the list shares this metric_key — review before addressing."
              >
                Possible dup
              </span>
            )}
            <div className="flex-1" />
            <button
              onClick={() => dismiss(iss)}
              disabled={busy[iss.id]}
              aria-label="Dismiss issue for this week"
              data-testid={`l10-ids-dismiss-${iss.id}`}
              className="text-[color:var(--cc-ink-muted)] hover:text-[#c73535] p-1 disabled:opacity-40"
            >
              <X size={14} />
            </button>
          </div>
          <div className="text-[13.5px] text-[#0e1420] font-semibold mb-2">{iss.body}</div>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)]">
              Suggested owner:
            </div>
            {isEditingOwner ? (
              <>
                <input
                  type="text"
                  value={ownerDraft}
                  onChange={(e) => setOwnerDraft(e.target.value)}
                  placeholder="Owner name..."
                  data-testid={`l10-ids-owner-input-${iss.id}`}
                  className="w-40 bg-white border border-[#0072bc] rounded px-2 py-0.5 text-[12px] text-[#0e1420] outline-none"
                />
                <button
                  onClick={() => saveOwnerEdit(iss)}
                  data-testid={`l10-ids-owner-save-${iss.id}`}
                  className="text-[10.5px] font-semibold px-2 py-0.5 rounded bg-[#0e8a68] text-white hover:bg-[#0b6e53]"
                >
                  Save
                </button>
                <button
                  onClick={cancelOwnerEdit}
                  data-testid={`l10-ids-owner-cancel-${iss.id}`}
                  className="text-[10.5px] font-semibold px-2 py-0.5 rounded border border-[#e7e9ee] text-[#0e1420] hover:bg-white"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <span className="font-mono-data text-[11.5px] text-[#0e1420]">
                  {iss.suggested_owner || <em className="text-[color:var(--cc-ink-muted)]">unassigned</em>}
                </span>
                <button
                  onClick={() => startOwnerEdit(iss)}
                  aria-label="Edit suggested owner"
                  data-testid={`l10-ids-owner-edit-${iss.id}`}
                  className="text-[color:var(--cc-ink-muted)] hover:text-[#0072bc] p-0.5"
                >
                  <Pencil size={11} />
                </button>
              </>
            )}
          </div>
          <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1">Solve</div>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              placeholder="Enter action / decision..."
              defaultValue={iss.resolution || ""}
              onChange={(e) => setDraft(iss.id, e.target.value)}
              onBlur={(e) => {
                if (e.target.value !== (iss.resolution || "")) {
                  api.patch(`/l10/ids/${iss.id}`, { resolution: e.target.value });
                }
              }}
              data-testid={`l10-ids-resolution-${iss.id}`}
              className="flex-1 min-w-[220px] bg-white border border-[#e7e9ee] rounded px-3 py-2 text-[13px] text-[#0e1420] outline-none focus:border-[#0072bc]"
            />
            <button
              onClick={() => promote(iss)}
              disabled={iss.promoted_to_todo_id || busy[iss.id]}
              data-testid={`l10-ids-promote-${iss.id}`}
              className="text-[12px] font-semibold px-3 py-2 rounded bg-[#0072bc] text-white hover:bg-[#005c99] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 whitespace-nowrap"
            >
              {iss.promoted_to_todo_id ? "✓ To-Do" : "→ To-Do"}
            </button>
          </div>
        </div>
      );
      })}
    </div>
  );
}

// ================= Conclude =================
function ConcludeBody({ meeting, setMeeting, currentWeekOf, selectedWeek, onImportedTodos }) {
  // Controlled state for the two importable fields. This lets file-import
  // pre-fill them locally without touching the DB. The user then reviews
  // and clicks an explicit "Save meeting fields" button (which appears
  // only when the local state has diverged from the persisted meeting)
  // — the human-review gate the scope doc mandates.
  const [cascadeDraft, setCascadeDraft] = useState(meeting?.cascade_notes || "");
  const [nextDateDraft, setNextDateDraft] = useState(meeting?.next_meeting_date || "");
  useEffect(() => { setCascadeDraft(meeting?.cascade_notes || ""); }, [meeting?.cascade_notes]);
  useEffect(() => { setNextDateDraft(meeting?.next_meeting_date || ""); }, [meeting?.next_meeting_date]);

  const fileInputRef = useRef(null);
  const [importErrors, setImportErrors] = useState(null);
  const [pendingImport, setPendingImport] = useState(null); // set when week mismatches, awaiting user confirm

  const save = async (updates) => {
    // Meeting PATCH must target the currently-viewed week — critical
    // for the "same-day entry" workflow where the user is on
    // `?week=YYYY-MM-DD` for the current-in-progress week rather than
    // the default latest_reporting resolution.
    const { data } = await api.patch("/l10/meeting", updates, { params: { week: selectedWeek } });
    setMeeting(data);
  };

  const cascadeDirty = cascadeDraft !== (meeting?.cascade_notes || "");
  const nextDateDirty = nextDateDraft !== (meeting?.next_meeting_date || "");
  const anyPrefillDirty = cascadeDirty || nextDateDirty;

  const savePrefill = async () => {
    const updates = {};
    if (cascadeDirty) updates.cascade_notes = cascadeDraft;
    if (nextDateDirty) updates.next_meeting_date = nextDateDraft;
    if (Object.keys(updates).length > 0) await save(updates);
  };

  const applyImport = (parsed) => {
    setCascadeDraft(parsed.meeting.cascade_notes);
    setNextDateDraft(parsed.meeting.next_meeting_date || "");
    onImportedTodos(parsed.todos);
  };

  const handleFileSelected = async (evt) => {
    const file = evt.target.files?.[0];
    evt.target.value = ""; // reset so same filename can be re-selected later
    if (!file) return;
    let text;
    try {
      text = await file.text();
    } catch (e) {
      setImportErrors([`Could not read file: ${e.message}`]);
      return;
    }
    const result = parseL10ImportFile(text);
    if (!result.ok) {
      setImportErrors(result.errors);
      return;
    }
    if (result.data.week_of !== currentWeekOf) {
      setPendingImport(result.data);
      return;
    }
    applyImport(result.data);
  };

  return (
    <div className="mt-3">
      {/* Import from File — additive, unlocks the transcript-extractor workflow */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 mb-4 border-b border-[#eef1f6]">
        <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] max-w-xl">
          Import a Claude-extracted meeting JSON to pre-fill Cascade Notes, Next Meeting, and Todos.
          Nothing is saved until you click through the fields.
        </div>
        <div className="flex items-center gap-2">
          {anyPrefillDirty && (
            <button
              type="button"
              onClick={savePrefill}
              data-testid="l10-conclude-save-prefill-btn"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded bg-[#0e8a68] text-white hover:bg-[#0b6e53]"
            >
              <Check size={14} strokeWidth={2.4} />
              Save meeting fields
            </button>
          )}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="l10-conclude-import-btn"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded border border-[#0072bc] text-[#0072bc] hover:bg-[#e7f4fb]"
          >
            <Upload size={14} strokeWidth={2.2} />
            Import from File
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleFileSelected}
          className="hidden"
          data-testid="l10-conclude-import-input"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-2">Meeting Rating (1–10)</div>
          <div className="flex items-center gap-3">
            <input
              type="range" min="1" max="10" step="1"
              value={meeting?.rating ?? 5}
              onChange={(e) => save({ rating: Number(e.target.value) })}
              data-testid="l10-rating-slider"
              className="flex-1 accent-[#0072bc]"
            />
            <div className="w-9 text-center font-mono-data font-bold text-[20px] text-[#0072bc]">{meeting?.rating ?? 5}</div>
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Next Meeting Date</div>
            {nextDateDirty && (
              <span className="text-[9.5px] font-semibold uppercase tracking-wider bg-[#fff7d6] text-[#8a6100] border border-[#f0dfa2] rounded px-1.5 py-0.5"
                    data-testid="l10-next-meeting-dirty-pill">Unsaved</span>
            )}
          </div>
          <input
            type="date"
            value={nextDateDraft}
            onChange={(e) => {
              // Manual-path auto-save preserved (fires only on real user gesture;
              // import-path setState updates never dispatch a native change event).
              setNextDateDraft(e.target.value);
              save({ next_meeting_date: e.target.value });
            }}
            data-testid="l10-next-meeting"
            className="bg-white border border-[#e7e9ee] rounded px-3 py-2 text-[13px] text-[#0e1420] outline-none focus:border-[#0072bc]"
          />
        </div>
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Cascade Notes</div>
            {cascadeDirty && (
              <span className="text-[9.5px] font-semibold uppercase tracking-wider bg-[#fff7d6] text-[#8a6100] border border-[#f0dfa2] rounded px-1.5 py-0.5"
                    data-testid="l10-cascade-dirty-pill">Unsaved</span>
            )}
          </div>
          <textarea
            value={cascadeDraft}
            onChange={(e) => setCascadeDraft(e.target.value)}
            onBlur={(e) => {
              // Manual-path auto-save preserved: PATCH fires when the user
              // blurs after typing (identical behavior to pre-import build).
              // Import-path pre-fill (setState) never triggers blur.
              if (e.target.value !== (meeting?.cascade_notes || "")) {
                save({ cascade_notes: e.target.value });
              }
            }}
            rows={4}
            placeholder="Key decisions and messages to cascade to the team..."
            data-testid="l10-cascade-notes"
            className="w-full bg-white border border-[#e7e9ee] rounded px-3 py-2 text-[13px] text-[#0e1420] outline-none focus:border-[#0072bc] resize-none"
          />
        </div>
      </div>

      {importErrors && (
        <ImportErrorModal errors={importErrors} onClose={() => setImportErrors(null)} />
      )}
      {pendingImport && (
        <WeekMismatchModal
          fileWeek={pendingImport.week_of}
          currentWeek={currentWeekOf}
          onCancel={() => setPendingImport(null)}
          onProceed={() => {
            applyImport(pendingImport);
            setPendingImport(null);
          }}
        />
      )}
    </div>
  );
}

// ================= Import modals =================
function ImportErrorModal({ errors, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         data-testid="l10-import-error-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full p-5">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={20} className="text-[#c73535]" />
          <div className="font-display font-semibold text-[16px] text-[#0e1420]">Import failed</div>
        </div>
        <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mb-3">
          The file does not match the expected schema. Fix the following and try again — nothing was pre-filled.
        </div>
        <ul className="list-disc pl-5 text-[12.5px] text-[#0e1420] space-y-1 max-h-64 overflow-y-auto"
            data-testid="l10-import-error-list">
          {errors.map((e, i) => <li key={i}><code className="bg-[#f5f7fa] px-1 rounded">{e}</code></li>)}
        </ul>
        <div className="flex justify-end mt-4">
          <button onClick={onClose} data-testid="l10-import-error-close"
                  className="text-[12.5px] font-semibold px-3 py-1.5 rounded bg-[#0e1420] text-white hover:bg-[#000]">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function WeekMismatchModal({ fileWeek, currentWeek, onCancel, onProceed }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         data-testid="l10-import-week-mismatch-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={20} className="text-[#8a6100]" />
          <div className="font-display font-semibold text-[16px] text-[#0e1420]">Week mismatch</div>
        </div>
        <div className="text-[13px] text-[#0e1420] mb-4">
          The imported file is for week of <strong className="font-mono-data">{fileWeek}</strong>,
          but you&apos;re viewing week of <strong className="font-mono-data">{currentWeek}</strong>.
          Did you upload the wrong week&apos;s file?
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} data-testid="l10-import-week-mismatch-cancel"
                  className="text-[12.5px] font-semibold px-3 py-1.5 rounded border border-[#e7e9ee] text-[#0e1420] hover:bg-[#f5f7fa]">
            Cancel import
          </button>
          <button onClick={onProceed} data-testid="l10-import-week-mismatch-proceed"
                  className="text-[12.5px] font-semibold px-3 py-1.5 rounded bg-[#8a6100] text-white hover:bg-[#6b4d00]">
            Import anyway
          </button>
        </div>
      </div>
    </div>
  );
}

// ================= Thresholds modal =================
function ThresholdsModal({ thresholds, onClose, onSave }) {
  const [form, setForm] = useState(thresholds);
  const fields = [
    ["revenue_goal_weekly", "Revenue goal (weekly)", "$"],
    ["job_average_goal", "Job average goal", "$"],
    ["conversion_rate_goal", "Conversion rate goal", "%"],
    ["close_rate_goal", "Close rate goal", "%"],
    ["close_rate_watch", "Close rate watch (yellow)", "%"],
    ["cancellation_rate_goal", "Cancellation rate goal", "%"],
    ["cancellation_rate_watch", "Cancellation rate watch (yellow)", "%"],
    ["tech_weekly_goal", "Tech weekly goal", "$"],
    ["heartbeat_red_threshold", "Heartbeat red threshold", "$"],
    ["van_profit_threshold", "Van profit threshold", "$"],
    ["persistent_weeks", "Persistent weeks (N)", "count"],
  ];
  return (
    <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-6"
         onClick={onClose} data-testid="l10-thresholds-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl w-full max-w-[560px] max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="p-5 border-b border-[#eef1f6] flex items-center justify-between">
          <div className="font-display font-bold text-[17px] text-[#0e1420]">Scorecard Thresholds</div>
          <button onClick={onClose} aria-label="Close" className="p-1.5 rounded-md hover:bg-[#f5f6f8]">
            <X size={16} strokeWidth={1.6} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          {fields.map(([key, label, unit]) => (
            <div key={key} className="flex items-center justify-between gap-3">
              <label className="text-[13px] text-[#0e1420]">{label}</label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={form[key] ?? ""}
                  onChange={(e) => setForm({ ...form, [key]: Number(e.target.value) })}
                  data-testid={`l10-threshold-${key}`}
                  className="w-32 bg-white border border-[#e7e9ee] rounded px-2 py-1.5 text-[13px] text-right font-mono-data text-[#0e1420] outline-none focus:border-[#0072bc]"
                />
                <span className="text-[11px] font-mono-data text-[color:var(--cc-ink-muted)] w-8 text-right">{unit}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-[#eef1f6] flex justify-end gap-2">
          <button onClick={onClose}
                  className="text-[12.5px] font-semibold px-4 py-2 rounded border border-[#e7e9ee] text-[#0e1420] hover:bg-[#f5f6f8]">
            Cancel
          </button>
          <button onClick={() => onSave(form)} data-testid="l10-thresholds-save"
                  className="text-[12.5px] font-semibold px-4 py-2 rounded bg-[#0072bc] text-white hover:bg-[#005c99]">
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
